import { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Download, X, Smartphone, Monitor } from 'lucide-react';

export const InstallPWA = () => {
  const { language } = useLanguage();
  const [showModal, setShowModal] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);

  useEffect(() => {
    // Check if iOS
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent);
    setIsIOS(isIOSDevice);
    
    // Check if Android
    const isAndroidDevice = /Android/.test(navigator.userAgent);
    setIsAndroid(isAndroidDevice);

    // Listen for install prompt
    window.addEventListener('beforeinstallprompt', (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    });
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setDeferredPrompt(null);
      }
    } else {
      setShowModal(true);
    }
  };

  const texts = {
    en: {
      install: "Install App",
      title: "Install Our App",
      subtitle: "Get quick access from your home screen",
      iosTitle: "Install on iPhone/iPad",
      iosSteps: [
        "Open this website in Safari",
        "Tap the Share button (square with arrow)",
        "Scroll down and tap 'Add to Home Screen'",
        "Tap 'Add' in the top right corner"
      ],
      androidTitle: "Install on Android",
      androidSteps: [
        "Open this website in Chrome",
        "Tap the menu (3 dots) in the top right",
        "Tap 'Install app' or 'Add to Home Screen'",
        "Confirm the installation"
      ],
      desktopTitle: "Install on Desktop",
      desktopSteps: [
        "Look for the install icon in the address bar",
        "Click on it and confirm installation",
        "The app will open in its own window"
      ],
      close: "Close"
    },
    de: {
      install: "App Installieren",
      title: "Unsere App Installieren",
      subtitle: "Schneller Zugriff von Ihrem Startbildschirm",
      iosTitle: "Auf iPhone/iPad installieren",
      iosSteps: [
        "Öffnen Sie diese Website in Safari",
        "Tippen Sie auf die Teilen-Taste (Quadrat mit Pfeil)",
        "Scrollen Sie nach unten und tippen Sie auf 'Zum Home-Bildschirm'",
        "Tippen Sie oben rechts auf 'Hinzufügen'"
      ],
      androidTitle: "Auf Android installieren",
      androidSteps: [
        "Öffnen Sie diese Website in Chrome",
        "Tippen Sie auf das Menü (3 Punkte) oben rechts",
        "Tippen Sie auf 'App installieren'",
        "Bestätigen Sie die Installation"
      ],
      desktopTitle: "Auf Desktop installieren",
      desktopSteps: [
        "Suchen Sie das Installations-Symbol in der Adressleiste",
        "Klicken Sie darauf und bestätigen Sie",
        "Die App öffnet sich in einem eigenen Fenster"
      ],
      close: "Schließen"
    },
    ro: {
      install: "Instalează Aplicația",
      title: "Instalează Aplicația Noastră",
      subtitle: "Acces rapid de pe ecranul principal",
      iosTitle: "Instalare pe iPhone/iPad",
      iosSteps: [
        "Deschide acest site în Safari",
        "Apasă pe butonul Share (pătratul cu săgeată)",
        "Derulează și apasă 'Add to Home Screen'",
        "Apasă 'Add' în colțul din dreapta sus"
      ],
      androidTitle: "Instalare pe Android",
      androidSteps: [
        "Deschide acest site în Chrome",
        "Apasă pe meniu (3 puncte) în dreapta sus",
        "Apasă 'Instalează aplicația'",
        "Confirmă instalarea"
      ],
      desktopTitle: "Instalare pe Desktop",
      desktopSteps: [
        "Caută iconița de instalare în bara de adrese",
        "Click pe ea și confirmă instalarea",
        "Aplicația se va deschide într-o fereastră proprie"
      ],
      close: "Închide"
    }
  };

  const t = texts[language] || texts.en;

  return (
    <>
      {/* Install Button */}
      <button
        onClick={handleInstallClick}
        className="fixed bottom-24 right-6 z-40 flex items-center gap-2 px-4 py-3 bg-[#D4AF37] text-black font-semibold text-sm rounded-full shadow-lg hover:bg-[#C5A059] transition-all duration-300 hover:scale-105"
        data-testid="install-pwa-button"
      >
        <Download className="w-5 h-5" />
        <span className="hidden sm:inline">{t.install}</span>
      </button>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80" onClick={() => setShowModal(false)}>
          <div 
            className="bg-[#121212] border border-white/10 w-full max-w-lg max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-white/10">
              <div>
                <h2 className="text-2xl text-white" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  {t.title}
                </h2>
                <p className="text-sm text-[#A1A1AA] mt-1">{t.subtitle}</p>
              </div>
              <button 
                onClick={() => setShowModal(false)}
                className="p-2 text-[#A1A1AA] hover:text-white transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6">
              {/* iOS Instructions */}
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Smartphone className="w-6 h-6 text-[#D4AF37]" />
                  <h3 className="text-lg text-white font-medium">{t.iosTitle}</h3>
                </div>
                <ol className="space-y-2 ml-9">
                  {t.iosSteps.map((step, index) => (
                    <li key={index} className="text-[#A1A1AA] text-sm flex gap-2">
                      <span className="text-[#D4AF37] font-semibold">{index + 1}.</span>
                      {step}
                    </li>
                  ))}
                </ol>
              </div>

              {/* Android Instructions */}
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Smartphone className="w-6 h-6 text-[#D4AF37]" />
                  <h3 className="text-lg text-white font-medium">{t.androidTitle}</h3>
                </div>
                <ol className="space-y-2 ml-9">
                  {t.androidSteps.map((step, index) => (
                    <li key={index} className="text-[#A1A1AA] text-sm flex gap-2">
                      <span className="text-[#D4AF37] font-semibold">{index + 1}.</span>
                      {step}
                    </li>
                  ))}
                </ol>
              </div>

              {/* Desktop Instructions */}
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Monitor className="w-6 h-6 text-[#D4AF37]" />
                  <h3 className="text-lg text-white font-medium">{t.desktopTitle}</h3>
                </div>
                <ol className="space-y-2 ml-9">
                  {t.desktopSteps.map((step, index) => (
                    <li key={index} className="text-[#A1A1AA] text-sm flex gap-2">
                      <span className="text-[#D4AF37] font-semibold">{index + 1}.</span>
                      {step}
                    </li>
                  ))}
                </ol>
              </div>
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-white/10">
              <button
                onClick={() => setShowModal(false)}
                className="w-full py-3 bg-[#D4AF37] text-black font-semibold hover:bg-[#C5A059] transition-all duration-300"
              >
                {t.close}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default InstallPWA;
